<script setup lang="ts">
import type { CSSProperties } from 'vue';
import type { IInfoTooltipProps } from '@/shared/ui/InfoTooltip/model/types/infoTooltip';

withDefaults(defineProps<IInfoTooltipProps>(), {
  title: '',
  isShowCross: true,
});

const isShow = defineModel<boolean>({ default: false });

const VIEWPORT_PADDING = 8;

const rootElement: Ref<HTMLElement | null> = ref(null);
const offset: Ref<{ x: number; y: number }> = ref({ x: 0, y: 0 });

const clampStyle: ComputedRef<CSSProperties> = computed(() =>
  offset.value.x || offset.value.y
    ? { transform: `translate(${offset.value.x}px, ${offset.value.y}px)` }
    : {},
);

const onClose = (): void => {
  isShow.value = false;
};

const clampToViewport = (): void => {
  const el = rootElement.value;
  if (!isShow.value || !el) return;

  const rect = el.getBoundingClientRect();
  let dx = 0;
  let dy = 0;

  if (rect.left < VIEWPORT_PADDING) {
    dx = VIEWPORT_PADDING - rect.left;
  } else if (rect.right > window.innerWidth - VIEWPORT_PADDING) {
    dx = window.innerWidth - VIEWPORT_PADDING - rect.right;
  }

  if (rect.top < VIEWPORT_PADDING) {
    dy = VIEWPORT_PADDING - rect.top;
  } else if (rect.bottom > window.innerHeight - VIEWPORT_PADDING) {
    dy = window.innerHeight - VIEWPORT_PADDING - rect.bottom;
  }

  if (!dx && !dy) return;
  offset.value = { x: offset.value.x + dx, y: offset.value.y + dy };
};

useElementResize(rootElement, clampToViewport);

watch(isShow, (value) => {
  if (!value) offset.value = { x: 0, y: 0 };
});
</script>

<template>
  <div
    v-show="isShow"
    ref="rootElement"
    v-click-outside="onClose"
    class="info-tooltip"
    :style="clampStyle"
    data-testid="info-tooltip"
  >
    <button
      v-if="isShowCross"
      class="info-tooltip__close"
      type="button"
      aria-label="Закрыть подсказку"
      @click.stop="onClose"
    >
      <AppIcon name="Cross" />
    </button>
    <h4 v-if="title" class="info-tooltip__title">
      {{ title }}
    </h4>
    <slot name="tooltip-content"></slot>
  </div>
</template>

<style lang="less">
.info-tooltip {
  .box-shadow();

  position: relative;
  background: #fff;
  max-width: 325px;
  z-index: 990;
  padding: @size-x4 @size-x8;

  &__close {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 14px;
    height: 14px;

    path {
      stroke: @color-elephant;
    }
  }

  &__title {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 700;
    margin-bottom: @size-x2;
  }
}
</style>
